/**
 * `/workflows/deal-screening/:workflowId` — one firm's deal-screening workflow.
 *
 * The Deals home (DOP-615) has three tabs:
 *  - Deals (`data`): three tiles saying what needs you, what is being
 *    written and what is ready, then one row per deal with its status in
 *    plain words and one next-step button. "Screen a deal" opens the dialog
 *    that adds deals and starts them.
 *  - Needs you (`review`): every open blocking gap, conflict, gap and omitted
 *    section across the deals' latest memos, each linking into the run where
 *    it is resolved. It is derived by the same `reviewItems` the run page
 *    uses.
 *  - How memos are written (`settings`): the recipe.
 *
 * A deal's memo versions live on the memo's own Versions tab, so the
 * workflow no longer has a History tab. A stale `?tab=history` link opens
 * Deals.
 *
 * Writes answer through the toast, like the monitoring page: an add or a run
 * request is a notice, and the list underneath re-renders with the truth.
 */

import { Plus } from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import DashboardShell, {
	SHELL_TAB_PARAM,
} from "../../components/Dashboards/DashboardShell";
import DealScreeningSettings from "../../components/DealScreening/DealScreeningSettings";
import DealsNeedsReview, {
	reviewGroups,
} from "../../components/DealScreening/DealsNeedsReview";
import DealScreeningTour from "../../components/DealScreening/DealScreeningTour";
import DealsSummaryTiles from "../../components/DealScreening/DealsSummaryTiles";
import TakeTourButton from "../../components/Tour/TakeTourButton";
import {
	DEAL_SCREENING_TOUR_SEEN_KEY,
	useWorkflowTour,
} from "../../hooks/useWorkflowTour";
import DealsTable, { sharedWithMe } from "../../components/DealScreening/DealsTable";
import { useOptionalAuthUser } from "../../context/AuthContext";
import { countLabel } from "../../components/DealScreening/dealScreeningFormat";
import ScreenDealDialog from "../../components/DealScreening/ScreenDealDialog";
import ErrorDisplay from "../../components/ErrorDisplay";
import { useToast } from "../../context/ToastContext";
import { useDealScreeningDeals } from "../../hooks/useDealScreeningDeals";
import { useDealScreeningPerDeal } from "../../hooks/useDealScreeningPerDeal";
import { useDealScreeningWorkflow } from "../../hooks/useDealScreeningWorkflow";
import {
	DealScreeningConflictError,
	dealScreeningService,
} from "../../services/dealScreeningService";
import type {
	DealScreeningCreateDealPayload,
	DealScreeningDeal,
	DealScreeningRecipeEdits,
	DealScreeningRun,
} from "../../types/dealScreening";
import { friendlyErrorMessage } from "../../utils/apiErrorDetail";

/**
 * A 409 is the server's sentence about the user's own action ("this folder is
 * already a deal") and is shown verbatim; anything else goes through the
 * deny-list. The same rule as the monitoring pages.
 */
const writeErrorMessage = (caught: unknown, fallback: string): string =>
	caught instanceof DealScreeningConflictError
		? caught.message
		: friendlyErrorMessage(caught, fallback);

/**
 * The latest memo of a deal that has something open, or is still a draft. A
 * deal that never ran, a reviewed memo with nothing open, or a memo still
 * generating costs no request. Module-level so
 * its identity is stable (`useDealScreeningPerDeal` keys its effect on it).
 */
const loadRunForReview = (
	deal: DealScreeningDeal,
): Promise<DealScreeningRun> | null => {
	const run = deal.latest_run;
	if (!run || run.generating) return null;
	const open =
		run.blocking_open + run.conflicts_open + run.gaps_open +
		(run.counts.sections_omitted ?? 0);
	// A draft with nothing open in its summary is read too (DOP-659): a section
	// set to appear in every memo that a person emptied in edit mode blocks
	// review, and only the full run shows it. A reviewed memo is settled.
	const draft = run.status !== "reviewed" && run.generated_at !== null && !run.generation_error;
	return open > 0 || draft ? dealScreeningService.getRun(run.public_id) : null;
};

const Skeleton: React.FC = () => (
	<div className="h-[420px] animate-pulse rounded-lg border border-gray-200 bg-white" />
);

const DealScreeningWorkflowPage: React.FC = () => {
	const { workflowId } = useParams<{ workflowId: string }>();
	const [searchParams, setSearchParams] = useSearchParams();
	const { showToast } = useToast();

	const {
		workflow,
		folderCandidates,
		foldersLoading,
		foldersError,
		loading,
		error,
		refresh,
		reloadFolders,
		updateWorkflow,
	} = useDealScreeningWorkflow(workflowId);
	const {
		deals,
		loading: dealsLoading,
		error: dealsError,
		stale,
		refresh: refreshDeals,
		addDeal,
		renameDeal,
		removeDeal,
		runDeal,
	} = useDealScreeningDeals(workflowId);

	// Needs review is read whatever the tab: its count is on the tab strip,
	// and a badge is only claimed once the memos behind it have answered.
	const review = useDealScreeningPerDeal(deals, !dealsLoading, loadRunForReview);
	const groups = useMemo(() => reviewGroups(review.results), [review.results]);
	const reviewCount = groups.reduce((sum, group) => sum + group.items.length, 0);
	// Required sections still to write block a memo too (DOP-659); the deals
	// table counts them with the blocking fields.
	const sectionsToWrite = useMemo(
		() =>
			Object.fromEntries(
				groups.map((group) => [
					group.run.public_id,
					group.items.filter((item) => item.kind === "required").length,
				]),
			),
		[groups],
	);
	const [screening, setScreening] = useState(false);
	// Shared memos: a teammate's list says which deals are theirs and which
	// are shared with them, and can show one kind at a time.
	const user = useOptionalAuthUser();
	const meId = user?.id ?? null;
	const sharing = deals.some((deal) => deal.owner_user_id);
	const [dealsChip, setDealsChip] = useState<"all" | "mine" | "shared">("all");
	const sharedCount = useMemo(() => deals.filter((deal) => sharedWithMe(deal, meId)).length, [deals, meId]);
	const shownDeals = useMemo(
		() =>
			dealsChip === "all"
				? deals
				: deals.filter((deal) => (dealsChip === "shared") === sharedWithMe(deal, meId)),
		[deals, dealsChip, meId],
	);
	const openTab = useCallback(
		(id: string) => {
			const next = new URLSearchParams(searchParams);
			next.set(SHELL_TAB_PARAM, id);
			setSearchParams(next);
		},
		[searchParams, setSearchParams],
	);
	// The tour points at the Deals tab, so it starts on its own only there,
	// once the deals have loaded.
	const onDealsTab = (searchParams.get(SHELL_TAB_PARAM) ?? "data") === "data";
	const tour = useWorkflowTour(
		DEAL_SCREENING_TOUR_SEEN_KEY,
		onDealsTab && workflow !== null && !dealsLoading,
	);

	const [runningIds, setRunningIds] = useState<ReadonlySet<string>>(
		() => new Set<string>(),
	);
	const handleRun = useCallback(
		async (dealId: string) => {
			setRunningIds((current) => new Set(current).add(dealId));
			try {
				const result = await runDeal(dealId);
				showToast(
					result.queued
						? `Screening ${result.run.deal.name} — memo v${result.run.version}.`
						: `${result.run.deal.name} is already being screened.`,
					result.queued ? "success" : "info",
				);
			} catch (caught) {
				showToast(writeErrorMessage(caught, "Could not start the run."), "error");
			} finally {
				setRunningIds((current) => {
					const next = new Set(current);
					next.delete(dealId);
					return next;
				});
			}
		},
		[runDeal, showToast],
	);

	const handleStop = useCallback(
		async (runId: string) => {
			try {
				const stopped = await dealScreeningService.stopRun(runId);
				showToast(`Stopped ${stopped.deal.name}. Its last memo is unchanged.`, "success");
			} catch (caught) {
				showToast(writeErrorMessage(caught, "Could not stop the run."), "error");
			} finally {
				await refreshDeals();
			}
		},
		[refreshDeals, showToast],
	);

	const handleRename = useCallback(
		async (dealId: string, name: string) => {
			try {
				await renameDeal(dealId, name);
				return true;
			} catch (caught) {
				showToast(writeErrorMessage(caught, "Could not rename the deal."), "error");
				return false;
			}
		},
		[renameDeal, showToast],
	);

	const handleRemove = useCallback(
		async (dealId: string) => {
			try {
				await removeDeal(dealId);
				showToast("Deal removed. Its folder is untouched.", "success");
				return true;
			} catch (caught) {
				showToast(writeErrorMessage(caught, "Could not remove the deal."), "error");
				return false;
			}
		},
		[removeDeal, showToast],
	);

	/** The card's contract: never rejects, so a refused add keeps the form. */
	const handleCreateDeal = useCallback(
		async (payload: DealScreeningCreateDealPayload) => {
			try {
				const created = await addDeal(payload);
				showToast(
					payload.run_now
						? `Screening ${created.name}. The memo takes a few minutes.`
						: `${created.name} added.`,
					"success",
				);
				return { ok: true };
			} catch (caught) {
				showToast(writeErrorMessage(caught, "Could not add the deal."), "error");
				return { ok: false };
			}
		},
		[addDeal, showToast],
	);

	const handleSaveRecipe = useCallback(
		async (edits: DealScreeningRecipeEdits) => {
			try {
				const saved = await updateWorkflow({ recipe_edits: edits });
				showToast("Saved. The next run of each deal uses it.", "success");
				return true;
			} catch (caught) {
				showToast(writeErrorMessage(caught, "Could not save the recipe."), "error");
				return false;
			}
		},
		[updateWorkflow, showToast],
	);

	const usedFolderIds = useMemo(
		() => new Set(deals.map((deal) => deal.folder_id)),
		[deals],
	);

	if (loading && workflow === null) {
		return (
			<div className="h-full overflow-y-auto bg-gray-50 px-6 py-6">
				<Skeleton />
			</div>
		);
	}

	if (error && workflow === null) {
		return (
			<div className="h-full overflow-y-auto bg-gray-50 py-6">
				<ErrorDisplay error={error} onRetry={refresh} />
			</div>
		);
	}

	if (!workflow) return null;

	const meta = [
		countLabel(dealsLoading ? workflow.deals_count : deals.length, "deal"),
		"one memo per deal",
	].join(" · ");

	const tabs = [
		{ id: "data", label: "Deals" },
		{
			id: "review",
			label: "Gaps",
			...(!review.loading && !dealsLoading ? { count: reviewCount } : {}),
		},
		{ id: "settings", label: "How memos are written" },
	];

	return (
		<>
		<DashboardShell
			title={workflow.name}
			meta={meta}
			tabs={tabs}
			actions={
				<>
				<TakeTourButton
					onClick={() => {
						openTab("data");
						tour.start();
					}}
				/>
				<button
					type="button"
					onClick={() => setScreening(true)}
					data-tour="screen-deal"
					className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-[#004C4B] px-3.5 text-sm font-semibold text-white hover:bg-[#003a3a]"
				>
					<Plus className="h-4 w-4" aria-hidden />
					Screen a deal
				</button>
				</>
			}
		>
			{(activeTab) => {
				if (activeTab === "settings") {
					return (
						<DealScreeningSettings
							workflow={workflow}
							onSaveRecipe={handleSaveRecipe}
						/>
					);
				}
				if (activeTab === "review") {
					return (
						<DealsNeedsReview
							groups={groups}
							loading={review.loading || dealsLoading}
							error={review.error}
						/>
					);
				}
				return (
					<div className="space-y-[18px]">
						{tour.open && <DealScreeningTour onClose={tour.end} />}
						<p className="text-base text-[#5B6472]">
							Doppler reads a deal's files and writes a first-look memo. Every
							sentence links to the page it came from.
						</p>
						<DealsSummaryTiles
							deals={deals}
							onOpenNeedsYou={() => openTab("review")}
							// Only once the list has items: for a moment after the deals
							// load, the per-deal reads have not started and it says 0.
							listedCount={!review.loading && !dealsLoading && reviewCount > 0 ? reviewCount : null}
							sectionsToWrite={sectionsToWrite}
						/>
						<div className="min-w-0 space-y-3">
							{dealsError && (
								<p role="alert" className="text-sm text-red-600">
									Could not load the deals.{" "}
									<button
										type="button"
										onClick={() => void refreshDeals()}
										className="underline hover:no-underline"
									>
										Try again
									</button>
								</p>
							)}
							{sharing && sharedCount > 0 && (
								<div className="flex gap-1" role="group" aria-label="Show deals">
									{(
										[
											["all", `All (${deals.length})`],
											["mine", `Mine (${deals.length - sharedCount})`],
											["shared", `Shared with me (${sharedCount})`],
										] as const
									).map(([id, label]) => (
										<button
											key={id}
											type="button"
											aria-pressed={dealsChip === id}
											onClick={() => setDealsChip(id)}
											className={`h-7 rounded-full border px-3 text-xs font-medium ${
												dealsChip === id
													? "border-[#004C4B] bg-[#E6F3F3] text-[#004C4B]"
													: "border-gray-200 bg-white text-[#5B6472] hover:bg-gray-50"
											}`}
										>
											{label}
										</button>
									))}
								</div>
							)}
							{dealsLoading && deals.length === 0 ? (
								<Skeleton />
							) : (
								<DealsTable
									deals={shownDeals}
									meId={sharing ? meId : null}
									stale={stale}
									onRun={(dealId) => void handleRun(dealId)}
									onStop={handleStop}
									runningIds={runningIds}
									onRename={handleRename}
									onRemove={handleRemove}
									sectionsToWrite={sectionsToWrite}
								/>
							)}
						</div>
					</div>
				);
			}}
		</DashboardShell>
		{screening && (
			<ScreenDealDialog
				folders={folderCandidates}
				foldersLoading={foldersLoading}
				foldersError={foldersError}
				onRetryFolders={reloadFolders}
				usedFolderIds={usedFolderIds}
				onCreate={handleCreateDeal}
				onClose={() => setScreening(false)}
			/>
		)}
		</>
	);
};

export default DealScreeningWorkflowPage;
